import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import type { AnnouncementPosition } from '@chirpback/protocol';
import type { HostBridge } from '../../../../host.js';
import type { Palette } from '../../../../_theme/palette.js';

interface ToastShellProps {
  palette: Palette;
  bridge: HostBridge;
  position?: AnnouncementPosition;
  children: ReactNode;
  width?: number;
}

type ToastCorner = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

const DEFAULT_CORNER: ToastCorner = 'bottom-right';
const DEFAULT_WIDTH = 380;
// Keep in sync with SHADOW_PAD in bridge.ts — iframe is padded so the shadow can fade cleanly.
const SHADOW_PAD = 48;

export function ToastShell({
  palette,
  bridge,
  position,
  children,
  width = DEFAULT_WIDTH,
}: ToastShellProps): JSX.Element {
  const corner = normalizeCorner(position);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number | undefined>();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const height = Math.ceil(entries[0]?.contentRect.height ?? 0);
      if (height > 0) setMeasuredHeight(height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!measuredHeight) return;
    bridge.send('chirp:surface:placement', {
      layout: 'toast',
      position: corner,
      width,
      height: measuredHeight,
      inset: 16,
    });
    // Wait one paint for the iframe resize before fading in, so the toast doesn't drift from center.
    const revealTimer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(revealTimer);
  }, [bridge, corner, width, measuredHeight]);

  return (
    <div ref={contentRef} style={cardStyle(palette, corner, width, visible)}>
      {children}
    </div>
  );
}

function normalizeCorner(position: AnnouncementPosition | undefined): ToastCorner {
  if (position === 'top-left' || position === 'top-right' || position === 'bottom-left' || position === 'bottom-right') {
    return position;
  }
  return DEFAULT_CORNER;
}

function cardStyle(palette: Palette, corner: ToastCorner, width: number, visible: boolean): CSSProperties {
  const [vertical, horizontal] = corner.split('-') as ['top' | 'bottom', 'left' | 'right'];
  return {
    position: 'absolute',
    [vertical]: SHADOW_PAD,
    [horizontal]: SHADOW_PAD,
    width,
    maxWidth: `calc(100vw - ${SHADOW_PAD * 2 + 32}px)`,
    background: palette.background,
    color: palette.text,
    fontFamily: palette.font,
    borderRadius: palette.radiusLg,
    boxShadow:
      '0 12px 32px rgba(15,23,42,0.18), 0 2px 8px rgba(15,23,42,0.08)',
    overflow: 'hidden',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(8px)',
    transition: 'opacity 180ms ease-out, transform 200ms cubic-bezier(0.16, 1, 0.3, 1)',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  };
}
